/* eslint-disable no-underscore-dangle */
import {
  AppointmentStatus,
  AVAILABLE_APPOINTMENT_STATUSES,
} from '@bb/domain/dist/types';
import { Feature } from '@bb/automaton';
import { GetPermissionsAppointmentDTO } from '@bb/domain/dist/types/dto/AppointmentController/getPermissionsAppointment';
import { AppointmentController, ListingController } from 'api';
import React, { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { addWeeks } from 'date-fns';
import {
  isLimitedAccessOnNonActiveBrokerage,
  featureEnabled,
  getWithExpiry,
  setWithExpiry,
  SHOWINGS_CALENDAR_STORAGE_KEYS,
} from 'utils';
import { AuthContext } from 'contexts';
import { getListingAppointmentConfig } from 'api/AppointmentConfigController';
import AppointmentConfig from '@bb/domain/dist/types/AppointmentConfig';
import { Listing } from 'api/ListingController/getListing';
import { ES_DELAY, TIME_CHANGE_STATUSES } from './constants';
import { useSearchParams, useFetchAppointments } from './hooks';
import {
  ShowingCalendarSearchParams,
  ShowingCalendarMenuOption,
  ShowingsCalendarPageParams,
  AppointmentTypes,
  CalendarFilters,
  BuyerTableData,
  ListingTableData,
  InPersonTableData,
  VirtualTableData,
  CalendarType,
  AgentCalendarMenu,
  ListingCalendarMenu,
  StatusCounts,
} from './types';
import LoadingView from './LoadingView';

export type ShowingsCalendarContextValue = {
  calendarType: CalendarType;
  getSearchParam: (key: ShowingCalendarSearchParams) => string | string[];
  isFetching: boolean;
  isLoadingSearch: boolean;
  page?: number;
  selectedMenuOption: ShowingCalendarMenuOption;
  setMenuOption: React.Dispatch<
    React.SetStateAction<ShowingCalendarMenuOption>
  >;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  date?: [moment.Moment, moment.Moment];
  setDate?: React.Dispatch<
    React.SetStateAction<[moment.Moment, moment.Moment]>
  >;
  setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
  searchText?: string;
  setSearchText?: React.Dispatch<React.SetStateAction<string>>;
  listing?: Listing;
  hasFeature: (feature: Feature | `${Feature}`) => boolean;
  buyerActionCount: number;
  inPersonActionCount: number;
  listingActionCount: number;
  /**
   * If an action is taken without navigating the showings calendar page,
   * call this to re-fetch the appointment list and update counts
   * @param esDelay Artificial delay so that actions get propagated to ES data
   */
  refetchPage: (esDelay?: number) => Promise<void>;
  statuses?: AppointmentStatus[];
  setStatuses?: React.Dispatch<
    React.SetStateAction<ShowingsCalendarContextValue['statuses']>
  >;
  pendingStatus?: boolean;
  setPendingStatus?: React.Dispatch<React.SetStateAction<boolean>>;
  clearStatuses: () => void;
  appointmentList: AppointmentTypes;
  buyerAppointments: BuyerTableData;
  listingAppointments: ListingTableData;
  inPersonAppointments: InPersonTableData;
  virtualAppointments: VirtualTableData;
  /** Number of showings in the active calendar under the current filters */
  totalShowingsCount: number;
  permissionsMap: Record<string, GetPermissionsAppointmentDTO>;
  listingId: string;
  statusCount: StatusCounts;
  selectedAppointments?: string[];
  setSelectedAppointments?: React.Dispatch<
    React.SetStateAction<ShowingsCalendarContextValue['selectedAppointments']>
  >;
  calendarModalVisible: boolean;
  setCalendarModalVisible: React.Dispatch<
    React.SetStateAction<ShowingsCalendarContextValue['calendarModalVisible']>
  >;
  syncByListingModalVisible?: boolean;
  setSyncByListingModalVisible?: React.Dispatch<
    React.SetStateAction<
      ShowingsCalendarContextValue['syncByListingModalVisible']
    >
  >;
  distinctListings: string[];
  listingsToSync: string[];
  setListingsToSync: React.Dispatch<
    React.SetStateAction<ShowingsCalendarContextValue['listingsToSync']>
  >;
  isLimitedAccessOnNAB: boolean;
  appointmentConfig: AppointmentConfig | undefined;
  setAppointmentConfig: React.Dispatch<
    React.SetStateAction<AppointmentConfig | undefined>
  >;
};

export type ShowingsCalendarContextProviderProps = {
  calendarType: CalendarType;
  /** Artificial delay before fetching appointments so that ES data gets populated */
  wait: keyof typeof ES_DELAY;
};

const ShowingsCalendarContext = createContext<
  ShowingsCalendarContextValue | undefined
>(undefined);

export const useShowingsCalendarContext = () => {
  const showingsCalendarContext = useContext(ShowingsCalendarContext);

  if (showingsCalendarContext === undefined)
    throw new Error(
      'useShowingsCalendarContext must be used within a ShowingsCalendarContextProvider',
    );

  return showingsCalendarContext;
};

export const ShowingsCalendarContextProvider: React.FC<
  ShowingsCalendarContextProviderProps
> = props => {
  const { children, calendarType, wait } = props;
  const { getSearchParam } = useSearchParams<ShowingCalendarSearchParams>();
  const { page: initialPage, listingId } =
    useParams<ShowingsCalendarPageParams>();

  const { auth, orgStatus, orgPlan } = useContext(AuthContext);

  const isLimitedAccessOnNAB = isLimitedAccessOnNonActiveBrokerage(
    auth,
    orgStatus,
    orgPlan,
  );

  const defaultMenuOption =
    (calendarType === 'buyer' && AgentCalendarMenu.Buyer) ||
    (calendarType === 'la' && AgentCalendarMenu.Listing) ||
    ListingCalendarMenu.InPerson;

  const [statusCount, setStatusCount] = React.useState<StatusCounts>({});

  const [selectedMenuOption, setMenuOption] =
    React.useState<ShowingCalendarMenuOption>(defaultMenuOption);

  const [permissionsMap, setPermissionsMap] = React.useState({});
  const [appointmentConfig, setAppointmentConfig] =
    React.useState<AppointmentConfig>();

  const [isWaiting, setIsWaiting] = React.useState(Boolean(wait));
  const [isLoading, setIsLoading] = React.useState(Boolean(listingId));
  const [listing, setListing] = React.useState<Listing | null>(null);
  const [isLoadingSearch, setIsLoadingSearch] = React.useState(false);
  const [isFetching, setIsFetching] = React.useState(false);
  const [calendarModalVisible, setCalendarModalVisible] = React.useState(false);
  const [syncByListingModalVisible, setSyncByListingModalVisible] =
    React.useState(false);
  const [selectedAppointments, setSelectedAppointments] = React.useState<
    string[]
  >([]);
  const [listingsToSync, setListingsToSync] = React.useState<string[]>([]);
  // #region Filters
  const [buyerText, setBuyerText] = React.useState('');
  const chronologicalShowingsEnabled = featureEnabled('chronologicalShowings');
  const [buyerPendingStatus, setBuyerPendingStatus] = React.useState(() => {
    const stored = getWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.buyerPendingStatus,
    );
    if (stored !== null) {
      return stored === 'true';
    }
    return chronologicalShowingsEnabled;
  });
  const [buyerStatuses, setBuyerStatuses] = React.useState<
    CalendarFilters['statuses']
  >(() => {
    const stored = getWithExpiry(SHOWINGS_CALENDAR_STORAGE_KEYS.buyerStatuses);
    if (stored) {
      return stored.split(',');
    }
    return chronologicalShowingsEnabled ? ['confirm', 'time_change'] : [];
  });
  const today = moment().startOf('day');
  const sixMonthAgo = moment().subtract(5, 'month');
  const [buyerDate, setBuyerDate] = React.useState<CalendarFilters['date']>(
    chronologicalShowingsEnabled
      ? [today, undefined]
      : [sixMonthAgo, undefined],
  );
  const [buyerPage, setBuyerPage] = React.useState(
    parseInt(initialPage, 10) || 0,
  );

  const [listingText, setListingText] = React.useState('');
  const [listingPendingStatus, setListingPendingStatus] = React.useState(() => {
    const stored = getWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.listingPendingStatus,
    );
    if (stored !== null) {
      return stored === 'true';
    }
    return chronologicalShowingsEnabled;
  });
  const [listingStatuses, setListingStatuses] = React.useState<
    CalendarFilters['statuses']
  >(() => {
    const stored = getWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.listingStatuses,
    );
    if (stored) {
      return stored.split(',');
    }
    return chronologicalShowingsEnabled
      ? ['attempted', 'new', 'confirm', 'time_change']
      : [];
  });

  const [listingDate, setListingDate] = React.useState<CalendarFilters['date']>(
    chronologicalShowingsEnabled
      ? [today, undefined]
      : [sixMonthAgo, undefined],
  );
  const [listingPage, setListingPage] = React.useState(
    parseInt(initialPage, 10) || 0,
  );

  const [individualListingPage, setIndividualListingPage] = React.useState(
    parseInt(initialPage, 10) || 0,
  );

  const [individualListingText, setIndividualListingText] = React.useState('');
  const [individualListingStatuses, setIndividualListingStatuses] =
    React.useState<CalendarFilters['statuses']>(() => {
      const stored = getWithExpiry(
        SHOWINGS_CALENDAR_STORAGE_KEYS.individualListingStatuses,
      );
      if (stored) {
        return stored.split(',');
      }
      return chronologicalShowingsEnabled
        ? ['attempted', 'new', 'confirm', 'time_change']
        : [];
    });
  const [individualListingPendingStatus, setIndividualListingPendingStatus] =
    React.useState(() => {
      const stored = getWithExpiry(
        SHOWINGS_CALENDAR_STORAGE_KEYS.individualListingPendingStatus,
      );
      if (stored !== null) {
        return stored === 'true';
      }
      return chronologicalShowingsEnabled;
    });
  const [individualListingDate, setIndividualListingDate] = React.useState<
    CalendarFilters['date']
  >(
    chronologicalShowingsEnabled
      ? [today, undefined]
      : [sixMonthAgo, undefined],
  );

  const activeFilters = React.useMemo<CalendarFilters>(() => {
    if (selectedMenuOption === AgentCalendarMenu.Buyer) {
      return {
        searchText: buyerText,
        statuses: buyerStatuses,
        pendingStatus: buyerPendingStatus,
        date: buyerDate,
        page: buyerPage,
        setSearchText: setBuyerText,
        setStatuses: setBuyerStatuses,
        setPendingStatus: setBuyerPendingStatus,
        setPage: setBuyerPage,
        setDate: setBuyerDate,
      };
    }
    if (selectedMenuOption === AgentCalendarMenu.Listing) {
      return {
        searchText: listingText,
        statuses: listingStatuses,
        pendingStatus: listingPendingStatus,
        date: listingDate,
        page: listingPage,
        setSearchText: setListingText,
        setStatuses: setListingStatuses,
        setPendingStatus: setListingPendingStatus,
        setPage: setListingPage,
        setDate: setListingDate,
      };
    }
    return {
      searchText: individualListingText,
      statuses: individualListingStatuses,
      pendingStatus: individualListingPendingStatus,
      date: individualListingDate,
      page: individualListingPage,
      setSearchText: setIndividualListingText,
      setStatuses: setIndividualListingStatuses,
      setPendingStatus: setIndividualListingPendingStatus,
      setDate: setIndividualListingDate,
      setPage: setIndividualListingPage,
    };
  }, [
    buyerText,
    buyerStatuses,
    buyerPendingStatus,
    buyerDate,
    buyerPage,
    individualListingPage,
    individualListingText,
    individualListingStatuses,
    individualListingPendingStatus,
    individualListingDate,
    listingText,
    listingStatuses,
    listingPendingStatus,
    listingPage,
    listingDate,
    selectedMenuOption,
  ]);

  // #endregion Filters

  React.useEffect(() => {
    const oneWeekFromNow = addWeeks(new Date(), 1).getTime();
    setWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.buyerStatuses,
      buyerStatuses.join(','),
      oneWeekFromNow,
    );
    setWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.buyerPendingStatus,
      String(buyerPendingStatus),
      oneWeekFromNow,
    );
  }, [buyerStatuses, buyerPendingStatus]);

  React.useEffect(() => {
    const oneWeekFromNow = addWeeks(new Date(), 1).getTime();
    setWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.listingStatuses,
      listingStatuses.join(','),
      oneWeekFromNow,
    );
    setWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.listingPendingStatus,
      String(listingPendingStatus),
      oneWeekFromNow,
    );
  }, [listingStatuses, listingPendingStatus]);

  React.useEffect(() => {
    const oneWeekFromNow = addWeeks(new Date(), 1).getTime();
    setWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.individualListingStatuses,
      individualListingStatuses.join(','),
      oneWeekFromNow,
    );
    setWithExpiry(
      SHOWINGS_CALENDAR_STORAGE_KEYS.individualListingPendingStatus,
      String(individualListingPendingStatus),
      oneWeekFromNow,
    );
  }, [individualListingStatuses, individualListingPendingStatus]);

  React.useEffect(() => {
    if (wait) {
      const timerId = setTimeout(() => {
        setIsWaiting(false);
      }, ES_DELAY[wait]);
      return () => {
        clearTimeout(timerId);
      };
    }
  }, [wait]);

  React.useEffect(() => {
    setBuyerPage(0);
  }, [buyerText, buyerStatuses, buyerDate]);

  React.useEffect(() => {
    setListingPage(0);
  }, [listingText, listingStatuses, listingDate]);

  React.useEffect(() => {
    if (!listingId) return;
    let active = true;
    const getListingData = async () => {
      const [listingData, permissionsData, appointmentConfigData] =
        await Promise.all([
          ListingController.getListing(listingId),
          AppointmentController.getPermissionsListing(listingId),
          getListingAppointmentConfig(listingId),
        ]);
      if (active) {
        setListing(listingData);
        setPermissionsMap(permissionsData);
        setAppointmentConfig(appointmentConfigData);
        setIsLoading(false);
      }
    };
    getListingData();
    return () => {
      active = false;
    };
  }, [listingId]);

  React.useEffect(() => {
    if (selectedAppointments.length) setListingsToSync([]);
  }, [selectedAppointments]);

  React.useEffect(() => {
    if (listingsToSync.length) setSelectedAppointments([]);
  }, [listingsToSync]);

  const _setMenuOption = (menuOption: ShowingCalendarMenuOption) => {
    if (menuOption === selectedMenuOption) return;
    setIsFetching(true);
    setMenuOption(menuOption);
  };

  const hasFeature = (feature: Feature | `${Feature}`) =>
    (Boolean(auth?.assumingUserId || auth?.isAssumedNotBB) &&
      auth?.org.id === listing?.org.id) ||
    listing?.org.features?.includes(feature);

  const clearStatuses = React.useCallback(() => {
    if (activeFilters.setStatuses) {
      activeFilters.setStatuses([]);
      activeFilters.setPendingStatus(false);
    }
  }, [activeFilters]);

  // Similar hook for counts
  const {
    buyerAppointments,
    listingAppointments,
    inPersonAppointments,
    virtualAppointments,
    managedAccessAppointments,
    buyerActionCount,
    listingActionCount,
    inPersonActionCount,
    totalShowingsCount,
    refetch: refetchAppointments,
  } = useFetchAppointments({
    ...activeFilters,
    selectedMenuOption,
    listing,
    listingId,
    hasVirtualTour: Boolean(listing?.virtualTourUrl),
    shouldFetch: !isLoading && !isWaiting,
    setIsFetching,
    setPermissionsMap,
    setStatusCount,
    setSelectedAppointments,
  });

  const distinctListings = React.useMemo(
    () => [
      ...new Set(
        listingAppointments
          .filter(
            appt =>
              [
                ...AVAILABLE_APPOINTMENT_STATUSES,
                ...TIME_CHANGE_STATUSES,
              ].includes(appt.status) && !appt.passed,
          )
          .map(appt => appt.listing.id),
      ),
    ],
    [listingAppointments],
  );

  let appointmentList: AppointmentTypes = buyerAppointments;
  if (selectedMenuOption === AgentCalendarMenu.Listing)
    appointmentList = listingAppointments;
  if (selectedMenuOption === ListingCalendarMenu.InPerson)
    appointmentList = inPersonAppointments;
  if (selectedMenuOption === ListingCalendarMenu.Virtual)
    appointmentList = virtualAppointments;
  if (selectedMenuOption === AgentCalendarMenu.ManagedAccess)
    appointmentList = managedAccessAppointments;

  if (isLoading || isWaiting) {
    return <LoadingView />;
  }

  return (
    <ShowingsCalendarContext.Provider
      value={{
        appointmentList,
        buyerAppointments,
        listingAppointments,
        inPersonAppointments,
        virtualAppointments,
        buyerActionCount,
        calendarType,
        getSearchParam,
        inPersonActionCount,
        isFetching,
        isLoadingSearch,
        listingActionCount,
        listing,
        hasFeature,
        permissionsMap,
        selectedMenuOption,
        setMenuOption: _setMenuOption,
        setIsFetching,
        totalShowingsCount,
        refetchPage: refetchAppointments,
        listingId,
        statusCount,
        ...activeFilters,
        clearStatuses,
        selectedAppointments,
        setSelectedAppointments,
        calendarModalVisible,
        setCalendarModalVisible,
        syncByListingModalVisible,
        setSyncByListingModalVisible,
        distinctListings,
        listingsToSync,
        setListingsToSync,
        isLimitedAccessOnNAB,
        appointmentConfig,
        setAppointmentConfig,
      }}
    >
      {children}
    </ShowingsCalendarContext.Provider>
  );
};
